import type { Filters } from "utils/queryUtils";
import { createAsyncThunk } from "@reduxjs/toolkit";
import type { FetchActionPayload } from "interfaces";
import type { PdfOverview } from "features/Library/Pdfs/types/state";
import type { PdfOverviewRequest } from "features/Library/Pdfs/types/requests";
import { getPrefix } from "features/Application/slices/models";
import { ReducerEntityPrefixTypes, ReducerNamespaceTypes } from "enums/reducer";
import type { RootState } from "features/Application/globaltypes/redux";
import { pdfsAppliedFilterSelector, pdfSearchSelector } from "features/Library/Pdfs/selectors";
import { escapeTerm } from "utils/searchUtils";
import { DEFAULT_LOAD_COUNT } from "features/Library/Pdfs/types/constants";
import { formatFilters } from "utils/filterMapUtils";
import { pdfFilterTypes } from "features/Library/Pdfs/types/models";
import DataService from "features/Application/services/dataServices/typedDataService";

export interface PdfsRequest {
  appliedFilter: Filters;
  skip: number;
  top: number;
  term: string;
  sortBy?: string;
  sortOrder?: string;
}

const countHeaderName = process.env.REACT_APP_COUNT_HEADER_NAME as string;
export const sendGetPdfRequest = (payload: PdfsRequest, signal?: AbortSignal) => {
  const { appliedFilter, skip, top, term, sortBy, sortOrder } = payload;
  const filterQueryParams = formatFilters(appliedFilter, pdfFilterTypes);

  return DataService.content.getContent(
    {
      top,
      skip,
      sortBy,
      sortOrder,
      filters: filterQueryParams,
      term,
      showPurchased: true,
      contentType: "pdf",
    },
    signal,
  );
};
export const fetchOverviewPdfs = createAsyncThunk<FetchActionPayload<PdfOverview>, PdfOverviewRequest>(
  getPrefix({
    namespace: ReducerNamespaceTypes.Library,
    entity: ReducerEntityPrefixTypes.Pdfs,
    name: "overview",
  }),
  async (request: PdfOverviewRequest, { getState, signal }) => {
    const rootState = getState() as RootState;
    const appliedFilter = pdfsAppliedFilterSelector(rootState);
    const searchTerm = escapeTerm(pdfSearchSelector(rootState));

    const result = await sendGetPdfRequest(
      {
        appliedFilter,
        skip: request.skip ?? 0,
        top: request.top ?? DEFAULT_LOAD_COUNT,
        term: searchTerm ?? "",
        sortBy: request.sortBy,
        sortOrder: request.sortDirection,
      },
      signal,
    );

    const data = result.data.map((item) => ({ ...item, thumbnailUrl: item.bag?.thumbnailUrl! }));

    const recordsCount = Number.parseInt(result.headers[countHeaderName]);

    return {
      items: data,
      totalCount: recordsCount,
      append: request.append,
    };
  },
);
